import { useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useSupabase } from '~/integrations/supabase/provider.tsx';
import { useStore } from '~/store/index.ts';
import type { CachedLatestPriceResponse } from '~/store/price-cache.ts';

const PRICE_CACHE_RECONNECT_DELAY_MS = 3_000;

interface PriceCacheSocketMessage {
	readonly prices?: CachedLatestPriceResponse[];
	readonly reason?: string;
	readonly symbols?: string[];
	readonly type: 'authenticated' | 'connected' | 'error' | 'price_update' | 'snapshot';
}

function getPriceCacheWebSocketUrl(): string | null {
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		const apiUrl = new URL(window.ENV.API_URL);
		apiUrl.protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
		apiUrl.pathname = `${apiUrl.pathname.replace(/\/$/, '')}/price-cache/ws`;
		apiUrl.search = '';
		apiUrl.hash = '';
		return apiUrl.toString();
	} catch (error) {
		console.error('[PriceCache] Failed to build websocket URL', error);
		return null;
	}
}

export function StartInstrumentStoreManager({ children }: Readonly<{ children: React.ReactNode }>) {
	const { accessToken } = useSupabase();
	const { applyCachedLatestPrices, tickerList } = useStore(
		useShallow((state) => ({
			applyCachedLatestPrices: state.applyCachedLatestPrices,
			tickerList: state.tickerList,
		})),
	);
	const normalizedTickerList = useMemo(() => Array.from(new Set(tickerList.filter(Boolean))).sort(), [tickerList]);
	const normalizedTickerListKey = useMemo(() => normalizedTickerList.join('|'), [normalizedTickerList]);
	const latestTickerListRef = useRef<string[]>(normalizedTickerList);
	const applyCachedLatestPricesRef = useRef(applyCachedLatestPrices);
	const websocketRef = useRef<WebSocket | null>(null);
	const reconnectTimerRef = useRef<number | null>(null);
	const isAuthenticatedRef = useRef(false);
	const shouldReconnectRef = useRef(true);

	useEffect(() => {
		latestTickerListRef.current = normalizedTickerList;
	}, [normalizedTickerList]);

	useEffect(() => {
		applyCachedLatestPricesRef.current = applyCachedLatestPrices;
	}, [applyCachedLatestPrices]);

	useEffect(() => {
		if (!accessToken || normalizedTickerList.length === 0) {
			return;
		}

		let cancelled = false;
		const websocketUrl = getPriceCacheWebSocketUrl();

		function applyLatestPrices(prices: CachedLatestPriceResponse[]) {
			applyCachedLatestPricesRef.current(prices);
		}

		function clearReconnectTimer() {
			if (reconnectTimerRef.current !== null) {
				window.clearTimeout(reconnectTimerRef.current);
				reconnectTimerRef.current = null;
			}
		}

		function subscribeToLatestTickers() {
			const websocket = websocketRef.current;
			if (!websocket || websocket.readyState !== WebSocket.OPEN || !isAuthenticatedRef.current) {
				return;
			}

			websocket.send(
				JSON.stringify({
					symbols: latestTickerListRef.current,
					type: 'subscribe',
				}),
			);
		}

		function connect() {
			if (cancelled || !websocketUrl) {
				return;
			}

			shouldReconnectRef.current = true;
			const websocket = new WebSocket(websocketUrl);
			websocketRef.current = websocket;
			isAuthenticatedRef.current = false;

			websocket.addEventListener('open', () => {
				websocket.send(
					JSON.stringify({
						accessToken,
						type: 'authenticate',
					}),
				);
			});

			websocket.addEventListener('message', (event) => {
				if (cancelled) {
					return;
				}

				try {
					const message = JSON.parse(event.data) as PriceCacheSocketMessage;
					if (message.type === 'connected') {
						return;
					}

					if (message.type === 'authenticated') {
						isAuthenticatedRef.current = true;
						subscribeToLatestTickers();
						return;
					}

					if (message.type === 'error') {
						const shouldReconnect = message.reason !== 'invalid-access-token' && message.reason !== 'handshake-timeout';
						shouldReconnectRef.current = shouldReconnect;
						console.error('[PriceCache] Websocket server error', {
							authenticated: isAuthenticatedRef.current,
							reason: message.reason ?? 'unknown',
							tickerCount: latestTickerListRef.current.length,
							willReconnect: shouldReconnect,
						});
						websocket.close();
						return;
					}

					if ((message.type === 'snapshot' || message.type === 'price_update') && message.prices) {
						applyLatestPrices(message.prices);
					}
				} catch (error) {
					console.error('[PriceCache] Websocket message parse error', error);
				}
			});

			websocket.addEventListener('close', (event) => {
				console.warn('[PriceCache] Websocket closed', {
					authenticated: isAuthenticatedRef.current,
					code: event.code,
					reason: event.reason || undefined,
					tickerCount: latestTickerListRef.current.length,
					wasClean: event.wasClean,
					willReconnect: !cancelled && shouldReconnectRef.current,
				});

				if (websocketRef.current === websocket) {
					websocketRef.current = null;
				}
				isAuthenticatedRef.current = false;

				if (cancelled || !shouldReconnectRef.current) {
					return;
				}

				clearReconnectTimer();
				reconnectTimerRef.current = window.setTimeout(() => {
					connect();
				}, PRICE_CACHE_RECONNECT_DELAY_MS);
			});

			websocket.addEventListener('error', (error) => {
				console.error('[PriceCache] Websocket error', error);
			});
		}

		connect();

		return () => {
			cancelled = true;
			clearReconnectTimer();
			isAuthenticatedRef.current = false;
			shouldReconnectRef.current = false;
			websocketRef.current?.close();
			websocketRef.current = null;
		};
	}, [accessToken, normalizedTickerList.length]);

	useEffect(() => {
		if (!normalizedTickerListKey || !isAuthenticatedRef.current) {
			return;
		}

		const websocket = websocketRef.current;
		if (!websocket || websocket.readyState !== WebSocket.OPEN) {
			return;
		}

		websocket.send(
			JSON.stringify({
				symbols: normalizedTickerList,
				type: 'subscribe',
			}),
		);
	}, [normalizedTickerList, normalizedTickerListKey]);

	return children;
}
